import moment from 'moment';

import { currencyMap } from '../constants/currency-map';

export const BILLING_CYCLE_MONTHLY = 'monthly';
export const BILLING_CYCLE_SIX_MONTH = 'six_month';

/**
 * "$59.99" for USD; other currencies keep their code so a CAD "$" is not mistaken for USD.
 *
 * @param {?number} cents
 * @param {?string} currency
 * @returns {string}
 */
export const formatAmount = (cents, currency) => {
	if (cents == null) {
		return '';
	}

	const amount = (cents / 100).toFixed(2);
	const currencyData = currencyMap[currency];

	if (!currencyData) {
		return amount;
	}

	return currency === 'usd'
		? `${currencyData.symbol}${amount}`
		: `${currencyData.symbol}${amount} ${currencyData.abbreviation}`;
};

/**
 * The saving of six monthly payments versus one six-month payment, rounded to a whole percent:
 * 50 for the $59.99 intro price and 25 for the $89.99 regular one.
 *
 * @param {?number} monthlyCents
 * @param {?number} sixMonthCents
 * @returns {?number}
 */
export const savePercent = (monthlyCents, sixMonthCents) => {
	if (!monthlyCents || !sixMonthCents) {
		return null;
	}

	const percent = Math.round((1 - sixMonthCents / (monthlyCents * 6)) * 100);

	return percent > 0 ? percent : null;
};

/**
 * Everything the premium column prints for a billing cycle. The "per month equivalent" is
 * floored to the cent so $59.99 reads as $9.99/mo rather than rounding up to $10.00.
 *
 * @param {?object} option The six-month or monthly option from the plan store.
 * @param {?object} monthlyOption The monthly option, used for the struck-through comparison.
 * @returns {{name: string, price: string, suffix: string, strike: ?string, equivalent: ?string,
 *   ctaLabel: string, introNote: ?string}}
 */
export const premiumDisplay = (option, monthlyOption) => {
	if (option?.cycle !== BILLING_CYCLE_SIX_MONTH) {
		return {
			name: 'Premium',
			price: formatAmount(option?.amount, option?.currency),
			suffix: '/mo',
			strike: null,
			equivalent: null,
			ctaLabel: 'Upgrade to Premium',
			introNote: null,
		};
	}

	const showIntroNote = option.is_intro && option.regular_amount && option.intro_ends_on;

	return {
		name: 'God Mode',
		price: formatAmount(option.amount, option.currency),
		suffix: '/6mo',
		strike: monthlyOption?.amount ? `${formatAmount(monthlyOption.amount * 6, monthlyOption.currency)}/6mo` : null,
		equivalent: `${formatAmount(Math.floor(option.amount / 6), option.currency)}/mo equivalent`,
		ctaLabel: 'Unlock God Mode',
		introNote: showIntroNote
			? `Intro price active through ${moment(option.intro_ends_on, 'YYYY-MM-DD').format('MMM D')}, `
				+ `${formatAmount(option.regular_amount, option.currency)}/6mo after`
			: null,
	};
};
